import type React from "react";
import { useLayoutEffect, useMemo, useRef, useState } from "react";
import InlineSVG from "react-inlinesvg";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  RewriteSuggestionCardHost,
  RewriteSuggestionCarouselHost,
  RewriteSuggestionCarouselPagination,
} from "@sudowrite/shared-chat";
import { SharedChatThemeProvider } from "src/components/SharedChatThemeProvider";
import { cn } from "src/lib/cn";
import { SudoTooltip } from "src/sudokit/components/SudoTooltip";
import { sanitizeWriteCarouselSuggestionBody } from "~shared/writeCarouselSuggestionSanitize";
import {
  getWriteSuggestionCardHeader,
  type WriteSuggestionCardHeaderConfig,
} from "./writeSuggestionCardHeader";

function displaySuggestionText(suggestion: string): string {
  return sanitizeWriteCarouselSuggestionBody(suggestion);
}

type Props = {
  suggestions: string[];
  labels?: Array<string | undefined>;
  explanations?: string[];
  /** Proposal lifecycle, shared by streamed, read-only, and actionable cards. */
  status?: string;
  preserveExactText?: boolean;
  className?: string;
  layout?: "carousel" | "stack";
  writeSuggestionCardHeader?: WriteSuggestionCardHeaderConfig;
  onApplySuggestion?: (suggestion: string, index: number, renderedHtml?: string) => void;
  allowApply?: boolean;
  appliedSuggestionIndex?: number | null;
  onUndoAppliedSuggestion?: () => void | Promise<void>;
  onSendSuggestionToNewDocument?: (
    suggestion: string,
    index: number,
    renderedHtml?: string,
  ) => void | Promise<void>;
};

type ThumbValue = -1 | 0 | 1;

const focusRingClasses =
  "focus-visible:outline-none focus-visible:ring-2 " +
  "focus-visible:ring-[var(--primary-cta-high-contrast)] " +
  "focus-visible:ring-offset-2 " +
  "focus-visible:ring-offset-[var(--menu-color-fullscreen)]";

const isolateControlActivationKeys = (event: React.KeyboardEvent) => {
  if (event.key === "Enter" || event.key === " ") {
    event.stopPropagation();
  }
};

type SuggestionCardProps = {
  suggestion: string;
  preserveExactText?: boolean;
  index: number;
  copied: boolean;
  thumbState: ThumbValue;
  showWriteCardChrome?: boolean;
  headerLabel?: string;
  headerIconSrc?: string;
  onApplySuggestion?: (suggestion: string, index: number, renderedHtml?: string) => void;
  allowApply?: boolean;
  appliedSuggestionIndex?: number | null;
  onUndoAppliedSuggestion?: () => void | Promise<void>;
  onSendSuggestionToNewDocument?: (
    suggestion: string,
    index: number,
    renderedHtml?: string,
  ) => void | Promise<void>;
  onCopy: () => void;
  onThumbToggle: (updater: ThumbValue | ((previous: ThumbValue) => ThumbValue)) => void;
  renderedSuggestionRef?: React.RefObject<HTMLDivElement | null>;
};

function RewriteSuggestionCard({
  suggestion,
  preserveExactText = false,
  index,
  copied,
  thumbState,
  showWriteCardChrome = false,
  headerLabel,
  headerIconSrc,
  onApplySuggestion,
  allowApply = true,
  appliedSuggestionIndex,
  onUndoAppliedSuggestion,
  onSendSuggestionToNewDocument,
  onCopy,
  onThumbToggle,
  renderedSuggestionRef,
}: SuggestionCardProps) {
  const localRenderedSuggestionRef = useRef<HTMLDivElement | null>(null);
  const activeRenderedSuggestionRef = renderedSuggestionRef ?? localRenderedSuggestionRef;
  const [sendingToNewDocument, setSendingToNewDocument] = useState(false);
  const displayText = preserveExactText ? suggestion : displaySuggestionText(suggestion);

  const getRenderedSuggestionHtmlForInsert = () => {
    const root = activeRenderedSuggestionRef.current;
    if (!root) return undefined;
    const firstElement = root.firstElementChild as HTMLElement | null;
    if (
      firstElement
      && root.childElementCount === 1
      && typeof firstElement.className === "string"
      && firstElement.className.includes("prose")
    ) {
      return firstElement.innerHTML || undefined;
    }
    return root.innerHTML || undefined;
  };

  const handleApply = () => {
    if (appliedSuggestionIndex === index && onUndoAppliedSuggestion) {
      void onUndoAppliedSuggestion();
      return;
    }
    onApplySuggestion?.(displayText, index, getRenderedSuggestionHtmlForInsert());
  };
  const isAppliedSuggestion = appliedSuggestionIndex === index;

  const handleSendToNewDocument = async () => {
    if (!onSendSuggestionToNewDocument || sendingToNewDocument) return;
    setSendingToNewDocument(true);
    try {
      await onSendSuggestionToNewDocument(displayText, index, getRenderedSuggestionHtmlForInsert());
    } finally {
      setSendingToNewDocument(false);
    }
  };

  return (
    <>
      {showWriteCardChrome && headerLabel && headerIconSrc ? (
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <InlineSVG
              src={headerIconSrc}
              className="h-[18px] w-5 flex-shrink-0 [&_path]:fill-app-grey-0"
              aria-hidden
            />
            <span className="truncate text-sm font-semibold uppercase tracking-[0.1em] text-app-grey-0">
              {headerLabel}
            </span>
          </div>
          <SudoTooltip text="Create document" position="top" delay={500}>
            <button
              type="button"
              aria-label="Send suggestion to new document"
              disabled={sendingToNewDocument || !onSendSuggestionToNewDocument}
              className={cn(
                "bg-transparent border-0 p-0 m-0 shadow-none rounded-md flex-shrink-0",
                "hover:opacity-80 transition-opacity",
                sendingToNewDocument ? "opacity-50 cursor-not-allowed" : "",
                focusRingClasses,
              )}
              onClick={() => void handleSendToNewDocument()}
              onKeyDown={isolateControlActivationKeys}
            >
              <InlineSVG
                src="/assets/send-to-doc.svg"
                className="h-5 w-5 [&_path]:fill-app-grey-0"
              />
            </button>
          </SudoTooltip>
        </div>
      ) : null}

      <div
        ref={activeRenderedSuggestionRef}
        className={cn(
          "min-h-[96px]",
          showWriteCardChrome ? "text-app-primary-cta-high-contrast" : "text-app-grey-0",
        )}
      >
        <ReactMarkdown
          className={cn(
            // Scope Markdown styles here: the app reset removes block spacing, and
            // Tailwind's typography plugin is not enabled for the prose classes.
            "prose prose-sm max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&_p]:my-4 [&_p]:leading-7",
            "[&_strong]:font-bold [&_em]:italic [&_del]:line-through",
            "[&_:is(h1,h2,h3,h4,h5,h6)]:my-3 [&_:is(h1,h2,h3,h4,h5,h6)]:font-[inherit] [&_:is(h1,h2,h3,h4,h5,h6)]:font-semibold [&_:is(h1,h2,h3,h4,h5,h6)]:leading-snug",
            "[&_h1]:text-2xl [&_h2]:text-xl [&_h3]:text-lg [&_:is(h4,h5,h6)]:text-base",
            "[&_ul]:list-disc [&_ol]:list-decimal [&_ul]:pl-5 [&_ol]:pl-5",
            "[&_ul]:my-2 [&_ol]:my-2 [&_li]:my-1 [&_li]:leading-7",
            "[&_li]:[list-style:inherit] [&_li.task-list-item]:list-none",
            "[&_li>p]:my-0 [&_li>p+p]:mt-2",
            "[&_blockquote]:my-4 [&_blockquote]:border-l-2 [&_blockquote]:border-solid [&_blockquote]:border-app-grey-3 [&_blockquote]:pl-4",
            "[&_a]:text-app-primary-cta-high-contrast [&_a]:underline",
            "[&_code]:font-mono [&_code]:text-sm [&_code]:text-inherit [&_pre]:my-4 [&_pre]:overflow-x-auto [&_pre]:whitespace-pre-wrap",
            "[&_pre_code]:before:content-none [&_pre_code]:after:content-none",
            "[&_hr]:my-4 [&_hr]:border-0 [&_hr]:border-t [&_hr]:border-solid [&_hr]:border-app-grey-3",
            "[&_table]:my-4 [&_table]:w-full [&_th]:font-semibold [&_th]:text-left [&_:is(th,td)]:border [&_:is(th,td)]:border-solid [&_:is(th,td)]:border-app-grey-3 [&_:is(th,td)]:p-2",
          )}
          remarkPlugins={[remarkGfm]}
        >
          {displayText}
        </ReactMarkdown>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {allowApply || isAppliedSuggestion ? <button
            type="button"
            aria-label={isAppliedSuggestion ? "Undo applied suggestion" : "Apply suggestion"}
            data-action-state={isAppliedSuggestion ? "undo" : "apply"}
            className={cn(
              "bg-transparent border-0 p-0 m-0 shadow-none rounded-md",
              "hover:opacity-80 transition-opacity",
              focusRingClasses,
            )}
            onClick={handleApply}
            onKeyDown={isolateControlActivationKeys}
          >
            <InlineSVG
              src={isAppliedSuggestion
                ? "/assets/editor-toolbar/undo.svg"
                : "/assets/arrow.backward.circle.svg"}
              className={cn(
                "[&_path]:fill-app-grey-0",
                isAppliedSuggestion ? "h-5 w-5" : "h-[16px] w-[16px]",
              )}
            />
          </button> : null}
          <button
            type="button"
            aria-label="Copy suggestion"
            className={cn(
              "bg-transparent border-0 p-0 m-0 shadow-none rounded-md",
              "hover:opacity-80 transition-opacity",
              focusRingClasses,
            )}
            onKeyDown={isolateControlActivationKeys}
            onClick={onCopy}
          >
            <InlineSVG
              src={copied ? "/assets/check-simple.svg" : "/assets/duplicate.svg"}
              className="h-[20px] w-[20px] [&_path]:fill-app-grey-0"
            />
          </button>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            aria-label="Like suggestion"
            className={cn(
              "bg-transparent border-0 p-0 m-0 shadow-none rounded-md",
              "hover:opacity-80 transition-opacity",
              focusRingClasses,
            )}
            onKeyDown={isolateControlActivationKeys}
            onClick={() => onThumbToggle((previous) => (previous === 1 ? 0 : 1))}
          >
            <InlineSVG
              src={thumbState === 1 ? "/assets/thumbsup-filled.svg" : "/assets/thumbsup.svg"}
              className="h-[18px] w-[18px] [&_path]:fill-app-grey-0"
            />
          </button>
          <button
            type="button"
            aria-label="Dislike suggestion"
            className={cn(
              "bg-transparent border-0 p-0 m-0 shadow-none rounded-md",
              "hover:opacity-80 transition-opacity",
              focusRingClasses,
            )}
            onKeyDown={isolateControlActivationKeys}
            onClick={() => onThumbToggle((previous) => (previous === -1 ? 0 : -1))}
          >
            <InlineSVG
              src={thumbState === -1 ? "/assets/thumbsdown-filled.svg" : "/assets/thumbsdown.svg"}
              className="h-[18px] w-[18px] [&_path]:fill-app-grey-0"
            />
          </button>
        </div>
      </div>
    </>
  );
}

export function RewriteSuggestionCarousel({
  suggestions,
  labels,
  explanations,
  preserveExactText = false,
  className,
  layout = "carousel",
  writeSuggestionCardHeader,
  onApplySuggestion,
  allowApply: requestedAllowApply = true,
  status,
  appliedSuggestionIndex = null,
  onUndoAppliedSuggestion,
  onSendSuggestionToNewDocument,
}: Props) {
  const allowApply = requestedAllowApply && (!status || ["awaiting_consent", "ready_to_apply", "needs_review_again"].includes(status));
  const rootRef = useRef<HTMLDivElement | null>(null);
  const mountedRef = useRef(false);
  const copyTimersRef = useRef(new Set<ReturnType<typeof setTimeout>>());
  useLayoutEffect(() => {
    mountedRef.current = true;
    const timers = copyTimersRef.current;
    return () => {
      mountedRef.current = false;
      for (const timer of timers) clearTimeout(timer);
      timers.clear();
    };
  }, []);

  const renderedSuggestionRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    const root = rootRef.current;
    const active = document.activeElement;
    if (root && active instanceof HTMLElement && root.contains(active)) {
      active.blur();
    }
  }, []);

  const [index, setIndex] = useState(0);
  const [copiedByIndex, setCopiedByIndex] = useState<Record<number, true>>({});
  const [thumbByIndex, setThumbByIndex] = useState<Record<number, ThumbValue>>({});

  const total = suggestions.length;
  const safeIndex = Math.max(0, Math.min(index, total - 1));
  const current = suggestions[safeIndex] ?? "";
  const canGoPrev = safeIndex > 0;
  const canGoNext = safeIndex < total - 1;
  const copied = copiedByIndex[safeIndex];
  const thumbState = thumbByIndex[safeIndex] ?? 0;
  const showPagination = total > 1 && layout === "carousel";
  const showWriteCardChrome = layout === "stack" && !!writeSuggestionCardHeader;

  const counterLabel = useMemo(() => `${safeIndex + 1} of ${total}`, [safeIndex, total]);

  const applyThumbToggle = (
    cardIndex: number,
    updater: ThumbValue | ((previous: ThumbValue) => ThumbValue),
  ) => {
    setThumbByIndex((prev) => {
      const previous = prev[cardIndex] ?? 0;
      const value = typeof updater === "function" ? updater(previous) : updater;
      if (value === 0) {
        const rest = { ...prev };
        delete rest[cardIndex];
        return rest;
      }
      return { ...prev, [cardIndex]: value };
    });
  };

  const handleCopy = async (cardIndex: number, text: string) => {
    if (copiedByIndex[cardIndex]) return;
    await navigator.clipboard.writeText(text);
    if (!mountedRef.current) return;
    setCopiedByIndex((prev) => ({ ...prev, [cardIndex]: true }));
    const timer = setTimeout(() => {
      copyTimersRef.current.delete(timer);
      setCopiedByIndex((prev) => {
        const rest = { ...prev };
        delete rest[cardIndex];
        return rest;
      });
    }, 1200);
    copyTimersRef.current.add(timer);
  };

  if (!Array.isArray(suggestions) || suggestions.length === 0) return null;

  const carousel = layout === "stack" ? (
    <div ref={rootRef} data-reply-toggle-exempt="true">
      <RewriteSuggestionCarouselHost layout="stack" className={className}>
        {suggestions.map((suggestion, cardIndex) => {
          const cardHeader = writeSuggestionCardHeader
            ? getWriteSuggestionCardHeader(writeSuggestionCardHeader, cardIndex, suggestions.length)
            : null;
          return (
            <div
              key={`rewrite-suggestion-${cardIndex}`}
              className={explanations?.[cardIndex] && cardIndex < suggestions.length - 1 ? "pb-4" : undefined}
            >
              <RewriteSuggestionCardHost>
                <RewriteSuggestionCard
                  suggestion={suggestion}
                  preserveExactText={preserveExactText}
                  index={cardIndex}
                  copied={Boolean(copiedByIndex[cardIndex])}
                  thumbState={thumbByIndex[cardIndex] ?? 0}
                  showWriteCardChrome={showWriteCardChrome}
                  headerLabel={labels?.[cardIndex] || cardHeader?.label}
                  headerIconSrc={cardHeader?.iconSrc}
                  onApplySuggestion={onApplySuggestion}
                  allowApply={allowApply}
                  appliedSuggestionIndex={appliedSuggestionIndex}
                  onUndoAppliedSuggestion={onUndoAppliedSuggestion}
                  onSendSuggestionToNewDocument={onSendSuggestionToNewDocument}
                  onCopy={() => void handleCopy(cardIndex, preserveExactText ? suggestion : displaySuggestionText(suggestion))}
                  onThumbToggle={(updater) => applyThumbToggle(cardIndex, updater)}
                />
              </RewriteSuggestionCardHost>
              {explanations?.[cardIndex] ? (
                <p className="mt-3 text-base text-app-grey-0">{explanations[cardIndex]}</p>
              ) : null}
            </div>
          );
        })}
      </RewriteSuggestionCarouselHost>
    </div>
  ) : (
    <div ref={rootRef} data-reply-toggle-exempt="true">
      <RewriteSuggestionCarouselHost
        layout="carousel"
        className={className}
        pagination={showPagination ? (
          <RewriteSuggestionCarouselPagination
            label={counterLabel}
            canGoPrev={canGoPrev}
            canGoNext={canGoNext}
            onPrev={() => canGoPrev && setIndex((value) => value - 1)}
            onNext={() => canGoNext && setIndex((value) => value + 1)}
            prevIcon={(
              <InlineSVG
                src="/assets/chevron-right.svg"
                className="w-4 h-4 rotate-180 [&_path]:fill-app-grey-1"
              />
            )}
            nextIcon={(
              <InlineSVG
                src="/assets/chevron-right.svg"
                className="w-4 h-4 [&_path]:fill-app-grey-1"
              />
            )}
          />
        ) : undefined}
      >
        <RewriteSuggestionCard
          suggestion={current}
          preserveExactText={preserveExactText}
          index={safeIndex}
          copied={Boolean(copied)}
          thumbState={thumbState}
          onApplySuggestion={onApplySuggestion}
          allowApply={allowApply}
          appliedSuggestionIndex={appliedSuggestionIndex}
          onUndoAppliedSuggestion={onUndoAppliedSuggestion}
          onSendSuggestionToNewDocument={onSendSuggestionToNewDocument}
          onCopy={() => void handleCopy(safeIndex, preserveExactText ? current : displaySuggestionText(current))}
          onThumbToggle={(updater) => applyThumbToggle(safeIndex, updater)}
          renderedSuggestionRef={renderedSuggestionRef}
        />
      </RewriteSuggestionCarouselHost>
      {explanations?.[safeIndex] ? (
        <p className="mt-3 text-base text-app-grey-0">{explanations[safeIndex]}</p>
      ) : null}
    </div>
  );

  return <SharedChatThemeProvider>{carousel}</SharedChatThemeProvider>;
}

export default RewriteSuggestionCarousel;
